import { useRef, useEffect } from 'react';
import { useQuery } from '@tanstack/react-query';
import httpClient from '../../../httpClient';
import ChevronIcon from '../../../images/chevron-down-12.svg';
import styles from '../../../pages/ProductSelection.module.css';
import useProductSelectionStore from '../../../stores/useProductSelectionStore';

const filterPaperOption = (option) =>
  option?.text && option.text.toLowerCase().includes('canvas');

const FRAMED_CANVAS_PAPER_TYPE = 'Canvas - White Wrap - 0.75 inch bar';
const FRAMED_CANVAS_DISPLAY_NAME = 'Framed Canvas';

const CanvasFormInputs = ({ quantityRef }) => {
  const {
    form,
    setForm,
    defaultOptions,
    resetPrintSizeForm,
    setPrintSizeLoading,
    processPrintSizeData,
    setFinishingServiceLoading,
    setFrameInputsLoading,
    processFinishingCategoriesData,
    processFinishingServicesData,
    processFinishingDetailsData,
    processFinishingServiceChange,
    processFrameSelection,
    processFrameMatSelection,
    getBaseMeta,
    resetFormInputs,
    getMetaResets,
    productType,
    lineItem,
    isEditInitializing,
    setIsEditInitializing,
  } = useProductSelectionStore();

  const selectRef = useRef(null);

  const paperTypeSelectedValue = form?.inputs?.paperType?.selectedValue;
  const printSizeSelectedValue = form?.inputs?.printSize?.selectedValue;
  const finishingCategoryId = form?.meta?.finishingCategoryId;
  const finishingServiceInput = form?.inputs?.finishingService;
  const isFramedCanvas = [FRAMED_CANVAS_PAPER_TYPE, FRAMED_CANVAS_DISPLAY_NAME].includes(
    form?.inputs?.paperType?.selectedValueText,
  );

  useEffect(() => {
    if (productType !== 'canvas' || isFramedCanvas) return;
    if (!form?.meta?.requiredInputs?.length) return;

    setForm((prev) => ({
      ...prev,
      meta: {
        ...prev.meta,
        requiredInputs: [],
      },
    }));
  }, [form?.meta?.requiredInputs, isFramedCanvas, productType, setForm]);

  const printSizesQuery = useQuery({
    queryKey: ['printSizes', paperTypeSelectedValue],
    queryFn: () => httpClient.get(`products/${paperTypeSelectedValue}/print_sizes`),
    enabled: !!paperTypeSelectedValue && paperTypeSelectedValue !== 'default',
    staleTime: Infinity,
    cacheTime: 0,
    onSuccess: (response) => {
      processPrintSizeData(response);
    },
  });

  useEffect(() => {
    if (paperTypeSelectedValue === 'default') {
      resetPrintSizeForm();
    } else if (paperTypeSelectedValue && paperTypeSelectedValue !== 'default' && printSizesQuery.isLoading) {
      setPrintSizeLoading(true);
    }
  }, [paperTypeSelectedValue, printSizesQuery.isLoading]);

  const finishingCategoriesQuery = useQuery({
    queryKey: ['finishingCategories', printSizeSelectedValue],
    queryFn: () => httpClient.get(`products/print_sizes/${printSizeSelectedValue}/finishing_categories`),
    enabled: !!printSizeSelectedValue && printSizeSelectedValue !== 'default',
    staleTime: Infinity,
    cacheTime: 0,
    onSuccess: (response) => {
      processFinishingCategoriesData(response);
    },
  });

  useEffect(() => {
    if (printSizeSelectedValue && printSizeSelectedValue !== 'default' && finishingCategoriesQuery.isLoading) {
      setFinishingServiceLoading(true);
    }
  }, [printSizeSelectedValue, finishingCategoriesQuery.isLoading]);

  const finishingServicesQuery = useQuery({
    queryKey: ['finishingServices', printSizeSelectedValue, finishingCategoryId],
    queryFn: () =>
      httpClient.get(
        `products/print_sizes/${printSizeSelectedValue}/finishing_categories/${finishingCategoryId}/finishing_services`
      ),
    enabled: !!printSizeSelectedValue && !!finishingCategoryId,
    staleTime: Infinity,
    cacheTime: 0,
    onSuccess: (response) => {
      processFinishingServicesData(response);
    },
  });

  useEffect(() => {
    if (finishingCategoryId && finishingCategoryId !== 'default' && finishingServicesQuery.isLoading) {
      setFrameInputsLoading(true);
    }
  }, [finishingCategoryId, finishingServicesQuery.isLoading]);

  useQuery({
    queryKey: ['finishingDetailsOptions', form?.meta?.finishingServiceId],
    queryFn: () => httpClient.get(`products/${form.meta.finishingServiceId}/finishing_details`),
    enabled: !!form?.meta?.finishingServiceId,
    staleTime: Infinity,
    cacheTime: 0,
    onSuccess: (response) => {
      processFinishingDetailsData(response);
    },
  });

  useEffect(() => {
    const options = finishingServiceInput?.options;
    if (!options || options.length === 0) return;
    if (finishingServiceInput?.selectedValue && finishingServiceInput.selectedValue !== 'default') return;

    const framingOption = options.find((opt) => opt?.text === 'Framing');
    if (!framingOption) return;

    setForm((prev) => ({
      ...prev,
      inputs: {
        ...prev.inputs,
        finishingService: {
          ...prev.inputs.finishingService,
          selectedValue: framingOption.value,
          selectedValueText: framingOption.text,
        },
      },
    }));
  }, [finishingServiceInput?.options, finishingServiceInput?.selectedValue, setForm]);

  useEffect(() => {
    if (form?.inputs?.finishingService?.selectedValueText === undefined) return;
    processFinishingServiceChange(form.inputs.finishingService.selectedValueText);
  }, [form?.inputs?.finishingService?.selectedValueText]);

  useEffect(() => {
    if (form?.inputs?.matFormat?.selectedValue === undefined && form?.inputs?.frameType?.selectedValue === undefined) return;
    let finishingCategoryIdValue;
    let error;
    if (form.inputs.matFormat?.selectedValue !== undefined) {
      finishingCategoryIdValue = form.inputs.matFormat.selectedValue;
      error = 'Please fill Mat Size';
    } else if (form.inputs.frameType?.selectedValue !== undefined) {
      if (form.inputs.frameType.selectedValue === 'default') {
        setForm((prevState) => ({
          meta: {
            ...getBaseMeta(prevState),
            finishingCategoryId: undefined,
            finishingServiceId: undefined,
            finishingDetailId: undefined,
            requiredInputs: ['Frame Type'],
          },
          inputs: {
            paperType: prevState.inputs.paperType,
            printSize: prevState.inputs.printSize,
            finishingService: prevState.inputs.finishingService,
            frameType: prevState.inputs.frameType,
          },
        }));
        return;
      }
      finishingCategoryIdValue = form.inputs.frameType.selectedValue;
      error = 'Please fill Frame, Frame Mat, Acrylic and Frame Stain';
    }

    setForm((prevState) => ({
      meta: {
        ...getBaseMeta(prevState),
        finishingCategoryId: finishingCategoryIdValue,
        error,
      },
      inputs: prevState.inputs,
    }));
  }, [form?.inputs?.matFormat?.selectedValue, form?.inputs?.frameType?.selectedValue]);

  useEffect(() => {
    if (form?.inputs?.frame?.selectedValue === undefined) return;
    processFrameSelection(form.inputs.frame.selectedValue, form.inputs.frame.selectedValueText);
  }, [form?.inputs?.frame?.selectedValue]);

  useEffect(() => {
    if (form?.inputs?.frameMat?.selectedValue === undefined) return;
    processFrameMatSelection(form.inputs.frameMat.selectedValue, form.inputs.frameMat.selectedValueText);
  }, [form?.inputs?.frameMat?.selectedValue]);

  useEffect(() => {
    if (productType !== 'canvas') return;
    const options = form?.inputs?.frameMat?.options;
    if (!options || options.length === 0) return;
    if (form?.inputs?.frameMat?.selectedValue && form.inputs.frameMat.selectedValue !== 'default') return;

    const noMatOption =
      options.find((opt) => opt?.text?.toLowerCase().includes('no mat')) ||
      options.find((opt) => opt?.text?.toLowerCase() === 'none') ||
      options[0];
    if (!noMatOption) return;

    setForm((prev) => ({
      ...prev,
      inputs: {
        ...prev.inputs,
        frameMat: {
          ...prev.inputs.frameMat,
          selectedValue: noMatOption.value,
          selectedValueText: noMatOption.text,
        },
      },
    }));
  }, [form?.inputs?.frameMat?.options, productType]);

  useEffect(() => {
    if (productType !== 'canvas') return;
    const options = form?.inputs?.acrylic?.options;
    if (!options || options.length === 0) return;
    if (form?.inputs?.acrylic?.selectedValue && form.inputs.acrylic.selectedValue !== 'default') return;

    const noAcrylicOption =
      options.find((opt) => opt?.text?.toLowerCase().includes('no acrylic')) ||
      options.find((opt) => opt?.text?.toLowerCase() === 'none') ||
      options[0];
    if (!noAcrylicOption) return;

    setForm((prev) => ({
      ...prev,
      inputs: {
        ...prev.inputs,
        acrylic: {
          ...prev.inputs.acrylic,
          selectedValue: noAcrylicOption.value,
          selectedValueText: noAcrylicOption.text,
          selectedValuePrice: noAcrylicOption.price,
        },
      },
    }));
  }, [form?.inputs?.acrylic?.options, productType]);

  useEffect(() => {
    if (form?.inputs?.acrylic?.selectedValue === undefined && form?.inputs?.mountSize?.selectedValue === undefined) return;
    if (form?.inputs?.acrylic?.selectedValue === 'default') {
      setForm((prevState) => ({
        meta: {
          subtotal: prevState.meta.subtotal,
          xAspectRatio: prevState.meta.xAspectRatio,
          yAspectRatio: prevState.meta.yAspectRatio,
          aspectRatio: prevState.meta.aspectRatio,
          cropPositionX: prevState.meta.cropPositionX,
          cropPositionY: prevState.meta.cropPositionY,
          cropWidth: prevState.meta.cropWidth,
          cropHeight: prevState.meta.cropHeight,
          finishingCategoryId: prevState.meta.finishingCategoryId,
          requiredInputs: [
            'Acrylic',
            ...((prevState.inputs?.frameType?.selectedValueText === 'Hardwood Frame'
              || prevState.inputs?.frameType?.selectedValueText === 'Hardwood Float Canvas Frame')
              ? ['Frame Stain'] : [])
          ],
        },
        inputs: {
          paperType: prevState.inputs.paperType,
          printSize: prevState.inputs.printSize,
          finishingService: prevState.inputs.finishingService,
          frameType: prevState.inputs.frameType,
          frame: prevState.inputs.frame,
          frameMat: prevState.inputs.frameMat,
          acrylic: prevState.inputs.acrylic,
          ...((form.inputs?.frameType?.selectedValueText === 'Hardwood Frame'
            || form.inputs?.frameType?.selectedValueText === 'Hardwood Float Canvas Frame')
            && { frameStain: defaultOptions.frameStain }),
        },
      }));
      return;
    }

    let finishingServiceId;
    if (form?.inputs?.acrylic?.selectedValue !== undefined && form?.inputs?.acrylic?.selectedValue !== 'none' && form?.inputs?.acrylic?.selectedValue !== 'default') {
      finishingServiceId = form.inputs.acrylic.selectedValue;
    }

    setForm((prevState) => ({
      ...prevState,
      meta: {
        ...getBaseMeta(prevState),
        finishingServiceId: finishingServiceId ?? prevState.meta.finishingServiceId,
        requiredInputs: [],
      },
    }));
  }, [form?.inputs?.acrylic?.selectedValue]);

  useEffect(() => {
    if (form?.inputs?.frameStain?.selectedValue === undefined) return;
    const finishingDetailId = form.inputs.frameStain?.selectedValue;
    setForm((prevState) => ({
      meta: {
        ...getBaseMeta(prevState),
        finishingDetailId,
        requiredInputs: [],
      },
      inputs: prevState.inputs,
    }));
  }, [form?.inputs?.frameStain?.selectedValue]);

  const handleInputChange = (key, e) => {
    const input = form?.inputs?.[key];
    if (!input) return;

    if (lineItem && isEditInitializing) {
      setIsEditInitializing(false);
    }

    if (key === 'acrylic' && e.target.value === 'none') {
      setForm((prevState) => ({
        ...prevState,
        meta: {
          ...prevState.meta,
          finishingServiceId: prevState.inputs?.frame?.selectedValue || prevState.meta.finishingServiceId,
          finishingDetailId: prevState.inputs?.frameStain?.selectedValue || prevState.meta.finishingDetailId,
          requiredInputs: [
            ...(prevState.inputs?.frameType?.selectedValueText === 'Hardwood Frame' ? ['Frame Stain'] : [])
          ],
        },
        inputs: {
          ...prevState.inputs,
          acrylic: {
            ...prevState.inputs.acrylic,
            selectedValue: 'none',
            selectedValueText: 'None',
            selectedValuePrice: 0,
          },
          ...(prevState.inputs?.frameType?.selectedValueText === 'Hardwood Frame' && { frameStain: prevState.inputs.frameStain }),
        },
      }));
      return;
    }

    if (key === 'acrylic') {
      setForm((prevState) => ({
        meta: {
          ...prevState.meta,
          finishingServiceId: undefined,
          finishingDetailId: undefined,
        },
        inputs: {
          ...prevState.inputs,
          acrylic: {
            ...prevState.inputs.acrylic,
            selectedValue: e.target.value,
            selectedValueText: e.target.options[e.target.selectedIndex].text,
            selectedValuePrice: input.options.find(
              (option) => option?.value?.toString() === e.target.value
            )?.price,
          },
        },
      }));
      return;
    }

    if (key === 'frameMat' && e.target.value === 'none') {
      setForm((prevState) => ({
        ...prevState,
        inputs: {
          ...prevState.inputs,
          frameMat: {
            ...prevState.inputs.frameMat,
            selectedValue: 'none',
            selectedValueText: 'None',
          },
        },
      }));
      return;
    }

    if (key === 'frameStain' && e.target.value === 'none') {
      setForm((prevState) => ({
        ...prevState,
        meta: {
          ...prevState.meta,
          finishingServiceId: prevState.inputs?.frame?.selectedValue || prevState.inputs?.acrylic?.selectedValue || prevState.meta.finishingServiceId,
          finishingDetailId: undefined,
          requiredInputs: [],
        },
        inputs: {
          ...prevState.inputs,
          frameStain: {
            ...prevState.inputs.frameStain,
            selectedValue: 'none',
            selectedValueText: 'None',
          },
        },
      }));
      return;
    }

    setForm((prevState) => {
      const resetInputs = resetFormInputs(prevState.inputs, key);
      resetInputs[key] = {
        ...resetInputs[key],
        selectedValue: e.target.value,
        selectedValueText: e.target.options[e.target.selectedIndex].text,
        selectedValuePrice: input.options.find(
          (option) => option?.value?.toString() === e.target.value
        )?.price,
      };

      const newMeta = { ...prevState.meta };
      const metaResets = getMetaResets(key, prevState);
      if (metaResets.finishingCategoryId) newMeta.finishingCategoryId = undefined;
      if (metaResets.finishingServiceId) newMeta.finishingServiceId = undefined;
      if (metaResets.finishingDetailId) newMeta.finishingDetailId = undefined;

      if (input.options[0]?.xAspectRatio && input.options[0]?.yAspectRatio) {
        const selectedOption = input.options.find(
          (option) => option?.value?.toString() === e.target.value
        );
        newMeta.xAspectRatio = selectedOption?.xAspectRatio;
        newMeta.yAspectRatio = selectedOption?.yAspectRatio;
        newMeta.aspectRatio = selectedOption?.xAspectRatio / selectedOption?.yAspectRatio;
      }

      return { meta: newMeta, inputs: resetInputs };
    });
  };

  if (!form || !form.inputs) return null;

  const filterOption = (option, key) => {
    if (key === 'paperType') return filterPaperOption(option);
    return true;
  };

  return (
    <div className={styles.printsFormStack}>
      <div className="input-container quantity">
        <div className="label-container">
          <div className="div-block-150">
            <div className="sub-label-form">Quantity</div>
          </div>
        </div>
        <div className="form-block-input w-form">
          <div className="input-wrapper">
            <input
              ref={quantityRef}
              type="number"
              min="1"
              maxLength="256"
              name="field-4"
              data-name="Field 4"
              id="data-jscolor"
              defaultValue="1"
              className="text-field-2 data-jscolor w-input"
              pattern="[0-9]*"
              inputMode="numeric"
            />
          </div>
        </div>
      </div>

      {Object.keys(form.inputs)
        .filter((key) => {
          if (productType === 'canvas' && key === 'finishingService') return false;
          if (productType === 'canvas' && (key === 'frameMat' || key === 'acrylic')) return false;
          return !form.inputs[key]?.isHidden;
        })
        .map((key) => {
          const input = form.inputs[key];
          if (!input) return null;

          const optionsToRender = input.options;

          return (
            <div key={key} style={input.style}>
              <div className="input-container paper-type">
                <div className="label-container">
                  <div className="div-block-150">
                    {input.labelHtml ? (
                      <div
                        className="sub-label-form"
                        dangerouslySetInnerHTML={{ __html: input.labelHtml }}
                      />
                    ) : (
                      <div className="sub-label-form">
                        {key === 'printSize' ? 'CANVAS SIZE' : input.label}
                      </div>
                    )}
                  </div>
                </div>
                <div className="form-block-input w-form">
                  <form className="form-input select-field">
                    <select
                      ref={selectRef}
                      id="my-field-3"
                      name="field-3"
                      data-name="Field 3"
                      className={`form-select-field w-select select-field-wrapper ${styles.selectNoNativeArrow}`}
                      style={{ width: '100%' }}
                      onChange={(e) => handleInputChange(key, e)}
                      value={input.selectedValue || 'default'}
                      disabled={!!input.isDisabled}
                    >
                      {input.isLoading === true && (!input.options || input.options.length === 0) ? (
                        <option value="">Loading...</option>
                      ) : (
                        <option value="default">Choose One</option>
                      )}
                      {optionsToRender && optionsToRender.length > 0 && optionsToRender
                        .filter((option) => filterOption(option, key))
                        .filter((option) => !option.text?.endsWith('**'))
                        .map((option) => {
                          if (!option || !option.value || !option.text) return null;
                          const isSizeDropdown = key === 'printSize' || key === 'matSize' || key === 'mountSize';
                          const displayText = isSizeDropdown
                            ? option.text
                            : option.text.replace(/\(\$\d+(\.\d{1,2})?\)/g, '').trim();
                          const paperTypeDisplayText =
                            key === 'paperType' && option.text === FRAMED_CANVAS_PAPER_TYPE
                              ? 'Framed Canvas'
                              : displayText;
                          return (
                            <option key={option.value} value={option.value}>
                              {paperTypeDisplayText}
                            </option>
                          );
                        })
                        .filter(Boolean)
                      }
                    </select>
                    <img
                      src={ChevronIcon}
                      alt=""
                      style={{
                        position: 'absolute',
                        right: '12px',
                        top: '50%',
                        transform: 'translateY(-50%)',
                        pointerEvents: 'none',
                        width: '16px',
                        height: '16px',
                      }}
                    />
                  </form>
                </div>
              </div>
            </div>
          );
        })}
    </div>
  );
};

export default CanvasFormInputs;
